import { useMutation } from '@tanstack/react-query';
import clsx from 'clsx';
import { api, ApiError } from '../api/client';
import { useDeviceStore, type SettableStatus } from '../store/device';

const OPTIONS: SettableStatus[] = ['Available', 'Away', 'Offline'];

export function StatusToggle() {
  const status = useDeviceStore(s => s.status);
  const setStatus = useDeviceStore(s => s.setStatus);
  const audioUnlocked = useDeviceStore(s => s.audioUnlocked);
  const ready = useDeviceStore(s => s.ready);

  const mutation = useMutation({
    mutationFn: (next: SettableStatus) => api.setAgentStatus(next),
    onMutate: next => {
      const prev = useDeviceStore.getState().status;
      setStatus(next);
      return { prev };
    },
    onError: (_err, _next, ctx) => {
      if (ctx) setStatus(ctx.prev);
    },
  });

  function pick(next: SettableStatus) {
    if (next === status || mutation.isPending) return;
    mutation.mutate(next);
  }

  const isInCall = status === 'In-call';
  const blocked = !ready || isInCall;
  const errorMsg = mutation.error instanceof ApiError
    ? (mutation.error.body as { error?: string } | undefined)?.error ?? `error ${mutation.error.status}`
    : mutation.error instanceof Error ? mutation.error.message : undefined;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            Your status
            {isInCall && (
              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-100 text-red-700">
                In-call · auto
              </span>
            )}
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            {!ready
              ? 'Connecting…'
              : isInCall
                ? 'Status will return to Available when this call ends.'
                : 'Connected to Twilio · Agents marked Available receive incoming calls.'}
          </p>
        </div>
        <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
          {OPTIONS.map(opt => {
            const selected = opt === status;
            return (
              <button
                key={opt}
                type="button"
                onClick={() => pick(opt)}
                disabled={blocked || mutation.isPending || !audioUnlocked}
                className={clsx(
                  'px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
                  selected
                    ? opt === 'Available'
                      ? 'bg-emerald-100 text-emerald-800'
                      : opt === 'Away'
                        ? 'bg-amber-100 text-amber-800'
                        : 'bg-slate-200 text-slate-700'
                    : 'text-slate-500 hover:text-slate-800',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                )}
              >
                {opt}
              </button>
            );
          })}
        </div>
      </div>
      {errorMsg && <p className="mt-3 text-xs text-red-700">Couldn't change status: {errorMsg}</p>}
    </div>
  );
}
